<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  pipelineId: { type: [Number, String], required: true },
  columnId: { type: [Number, String], default: null },
});
const emit = defineEmits(['edit', 'create']);

const confirmingDeleteId = ref(null);

const store = useStore();
const { t } = useI18n();

const automations = computed(
  () => store.getters['kanbanAutomations/getAutomations']
);

const TRIGGER_LABELS = computed(() => ({
  card_created: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_CREATED'),
  card_moved: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_MOVED'),
  card_status_changed: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_STATUS_CHANGED'),
  tag_added: t('KANBAN.AUTOMATIONS.TRIGGERS.TAG_ADDED'),
  tag_removed: t('KANBAN.AUTOMATIONS.TRIGGERS.TAG_REMOVED'),
  card_stalled_no_move: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_STALLED_NO_MOVE'),
  card_stalled_no_status: t(
    'KANBAN.AUTOMATIONS.TRIGGERS.CARD_STALLED_NO_STATUS'
  ),
  card_stalled_no_interaction: t(
    'KANBAN.AUTOMATIONS.TRIGGERS.CARD_STALLED_NO_INTERACTION'
  ),
  card_closing_date: t('KANBAN.AUTOMATIONS.TRIGGERS.CARD_CLOSING_DATE'),
  task_completed: t('KANBAN.AUTOMATIONS.TRIGGERS.TASK_COMPLETED'),
  task_due_date: t('KANBAN.AUTOMATIONS.TRIGGERS.TASK_DUE_DATE'),
  meeting_scheduled: t('KANBAN.AUTOMATIONS.TRIGGERS.MEETING_SCHEDULED'),
  meeting_status_changed: t(
    'KANBAN.AUTOMATIONS.TRIGGERS.MEETING_STATUS_CHANGED'
  ),
  meeting_date: t('KANBAN.AUTOMATIONS.TRIGGERS.MEETING_DATE'),
}));

const triggerLabel = automation =>
  TRIGGER_LABELS.value[automation.trigger_type] ?? automation.trigger_type;

onMounted(() => {
  store.dispatch('kanbanAutomations/fetch', {
    pipelineId: props.pipelineId,
    columnId: props.columnId,
  });
});

function toggleActive(automation) {
  store.dispatch('kanbanAutomations/update', {
    id: automation.id,
    active: !automation.active,
  });
}

function remove(id) {
  store.dispatch('kanbanAutomations/delete', id);
  confirmingDeleteId.value = null;
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <button
      class="self-start rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
      @click="emit('create')"
    >
      {{ $t('KANBAN.AUTOMATIONS.NEW') }}
    </button>

    <ul v-if="automations.length" class="flex flex-col gap-1">
      <li
        v-for="automation in automations"
        :key="automation.id"
        class="flex items-center justify-between rounded-lg border border-n-weak bg-[#FEFEFE] dark:!bg-[#141517] px-3 py-2"
      >
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ automation.name }}
          </span>
          <span class="text-xs text-n-slate-10">{{
            triggerLabel(automation)
          }}</span>
        </div>
        <div class="flex items-center gap-3 flex-shrink-0">
          <button
            class="text-xs transition-colors"
            :class="
              automation.active
                ? 'text-n-teal-11'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="toggleActive(automation)"
          >
            {{
              automation.active
                ? $t('KANBAN.AUTOMATIONS.ACTIVE')
                : $t('KANBAN.AUTOMATIONS.INACTIVE')
            }}
          </button>
          <button
            class="text-xs text-n-slate-11 hover:text-n-brand transition-colors"
            @click="emit('edit', automation)"
          >
            {{ $t('KANBAN.AUTOMATIONS.EDIT') }}
          </button>
          <template v-if="confirmingDeleteId === automation.id">
            <span class="text-xs text-n-slate-11">
              {{ $t('KANBAN.AUTOMATIONS.DELETE_CONFIRM') }}
            </span>
            <button
              class="text-xs text-n-ruby-11 hover:text-n-ruby-10 transition-colors font-medium"
              @click="remove(automation.id)"
            >
              {{ $t('KANBAN.AUTOMATIONS.PANEL.DELETE_YES') }}
            </button>
            <button
              class="text-xs text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="confirmingDeleteId = null"
            >
              {{ $t('KANBAN.AUTOMATIONS.PANEL.DELETE_NO') }}
            </button>
          </template>
          <button
            v-else
            class="text-xs text-n-ruby-11 hover:text-n-ruby-10 transition-colors"
            @click="confirmingDeleteId = automation.id"
          >
            {{ $t('KANBAN.AUTOMATIONS.DELETE') }}
          </button>
        </div>
      </li>
    </ul>

    <p v-else class="text-sm text-n-slate-10 italic px-1">
      {{ $t('KANBAN.AUTOMATIONS.EMPTY') }}
    </p>
  </div>
</template>
